import { Box, Divider, Flex } from "@chakra-ui/react";
import { Fragment } from "react";
import { Location } from "react-router-dom";
import { isNotNull } from "src/services/predicates";
import { useLeftNav } from "./LeftNavContext";
import { NavGroupElement } from "./NavGroup";
import { NavItemElement } from "./NavItem";
import { Mode } from "./types";

type NavChildren =
  | (NavItemElement | null)[]
  | (NavGroupElement | null)[]
  | NavGroupElement
  | NavItemElement;

export type NavLevelElement = React.ReactElement<NavLevelProps>;
export interface NavLevelProps {
  level: number;
  id: string;
  children: NavChildren;
}

/**
 * Note: This component adds dividers between the provided children assuming
 * that each child represents meaningful content. If a child does not or should
 * be hidden, it must be the literal `null` -- an empty fragment (i.e. `<></>`)
 * or a component that renders nothing (e.g. `() => null` or `<WithUserPermissions>`)
 * will introduce unexpected dividers and spacing.
 */
export function NavLevel({ children, level }: NavLevelProps) {
  const { currentLevel, mode } = useLeftNav();
  return (
    <Box
      key={level}
      position="absolute"
      opacity={level !== currentLevel ? "0" : "1"}
      top={0}
      left={0}
      right={0}
      bottom={0}
      transform={`translateX(${(level - currentLevel) * 100}%)`}
      transition="transform 0.3s ease-in-out"
      bg="gray.50"
    >
      <Box
        padding="1.5rem"
        {...(mode === "compact"
          ? {
              width: "fit-content",
              paddingX: "1rem",
            }
          : {
              width: "20rem",
              overflowX: "hidden",
            })}
      >
        <Flex as="ul" direction="column" gap={8}>
          {withDivider(children, mode)}
        </Flex>
      </Box>
    </Box>
  );
}

const NavDivider = () => (
  <Divider
    aria-hidden="true"
    backgroundColor="primary.100"
    height="1px"
    opacity={0}
    animation="fadeIn 0.25s ease-in forwards"
    sx={{
      "@keyframes fadeIn": {
        "0%": { opacity: 0 },
        "100%": { opacity: 1 },
      },
    }}
  />
);

// Note: This function can produce unexpected results if `children` contains
// elements that do not render anything (e.g. `<></>`, `() => null`, or
// `<WithUserPermissions>`).
export function withDivider(children: NavChildren, mode: Mode) {
  if (!Array.isArray(children)) return children;
  return children
    .filter(isNotNull)
    .flatMap((child, index) => [
      ...(index > 0 && mode === "compact"
        ? [<NavDivider key={`divider-${index}`} />]
        : []),
      <Fragment key={`child-${index}`}>{child}</Fragment>,
    ]);
}

export function findCurrentLevel(
  location: Location,
  navLevel: NavLevelElement | NavLevelElement[]
): number {
  const list = Array.isArray(navLevel) ? navLevel : [navLevel];
  const currentNavLevel = list.find((nav) => isCurrentLevel(location, nav));
  return currentNavLevel?.props.level ?? 0;
}

export function isCurrentLevel(
  location: Location,
  navLevel: NavLevelElement
): boolean {
  const children = navLevel.props.children;
  return Boolean(findActiveNavItem(location, children));
}

type NavItemNode = NavItemElement | null | undefined | boolean;
function findActiveNavItem(
  location: Location,
  children:
    | NavItemNode
    | (NavItemNode | null)[]
    | NavGroupElement
    | (NavGroupElement | null)[]
): NavItemElement | undefined {
  if (!children || typeof children === "boolean") return undefined;

  const childrenList = Array.isArray(children) ? children : [children];

  for (const child of childrenList) {
    if (isActiveNavItem(location, child)) {
      return child as NavItemElement;
    }
  }

  return undefined;
}

function isActiveNavItem(
  location: Location,
  navItem: NavGroupElement | NavItemNode | null
): boolean {
  if (!navItem || typeof navItem === "boolean") {
    return false;
  }

  // Handle NavGroup (has children property)
  if ("children" in navItem.props) {
    // Recursively check children of NavGroup
    const activeChild = findActiveNavItem(location, navItem.props.children);
    return Boolean(activeChild);
  }

  // Handle NavItem (has url property)
  if ("url" in navItem.props) {
    return navItem.props.url === location.pathname;
  }

  return false;
}
